import { useState, useEffect, useRef } from 'react';
import { useUser } from '../context/UserContext';
import config from '../config';
import ImageItem from './ImageItem';

const ImageGallery = ({ images, setFilespec }) => {
    const { user } = useUser(); 
    const [scrollPosition, setScrollPosition] = useState(0);
    const [scrollUp, setScrollUp] = useState(true);
    const [autoScroll, setAutoScroll] = useState(true);
    const [marginTop, setMarginTop] = useState(0);
    const [isUserScrolling, setIsUserScrolling] = useState(false);
    const containerRef = useRef(null);
    const userScrollTimeoutRef = useRef(null);
    const touchStartRef = useRef(null);
  
    // Handle all forms of user scrolling (wheel, touch, keyboard)
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        
        const handleUserScroll = () => {
            setIsUserScrolling(true);
            setScrollPosition(container.scrollTop);
            
            // Clear any existing timeout
            if (userScrollTimeoutRef.current) {
                clearTimeout(userScrollTimeoutRef.current);
            }
            
            // Reset to auto-scrolling after 3 seconds of no user scrolling
            userScrollTimeoutRef.current = setTimeout(() => {
                setIsUserScrolling(false);
            }, 3000);
        };
        
        // Handle mouse wheel
        const handleWheel = () => handleUserScroll();
        
        // Handle touch start
        const handleTouchStart = (e) => {
        touchStartRef.current = e.touches[0].clientY;
        };
        
        // Handle touch move
        const handleTouchMove = () => handleUserScroll();
        
        // Handle keyboard navigation
        const handleKeyDown = (e) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || 
            e.key === 'PageUp' || e.key === 'PageDown' || 
            e.key === 'Home' || e.key === 'End' || e.key === ' ') {
            handleUserScroll();
        }
        };
        
        // Handle normal scroll events (covers all input methods)
        const handleScroll = () => {
            if (isUserScrolling) {
                setScrollPosition(container.scrollTop);
            }
        };
        
        // Add all event listeners
        container.addEventListener('wheel', handleWheel, { passive: true });
        container.addEventListener('touchstart', handleTouchStart, { passive: true });
        container.addEventListener('touchmove', handleTouchMove, { passive: true });
        container.addEventListener('keydown', handleKeyDown);
        container.addEventListener('scroll', handleScroll, { passive: true });
        
        return () => {
            // Remove all event listeners
            container.removeEventListener('wheel', handleWheel);
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchmove', handleTouchMove);
            container.removeEventListener('keydown', handleKeyDown);
            container.removeEventListener('scroll', handleScroll);
      
            if (userScrollTimeoutRef.current) {
                clearTimeout(userScrollTimeoutRef.current);
            }
        };
    }, [isUserScrolling]);
  
    useEffect(() => {
        setScrollPosition(0);
        setMarginTop(0);
    }, [images]);
  
    useEffect(() => {
        if (!autoScroll) return;

        const container = containerRef.current;
        if (!container || isUserScrolling) return;

        // Calculate the total scroll height
        let scrollHeight = container.scrollHeight - container.clientHeight;
        // console.log(container.scrollHeight, container.clientHeight)
        // console.log(scrollHeight)

        // do not scroll if 2 or less images
        // if ((scrollHeight > 0) && ((scrollHeight * 4) <= container.clientHeight)) return;
        // if (images.length <= 2) return;

        // Define scrolling speed (lower is slower)
        const scrollSpeed = 1; // pixels per frame
        
        let animationFrameId;
        let lastTimestamp = null;
        
        const scrollStep = (timestamp) => {
            if (!lastTimestamp) lastTimestamp = timestamp;
            const elapsed = timestamp - lastTimestamp;
            
            // Only update every 16ms for smooth 60fps scrolling
            if (elapsed > 16) {
                lastTimestamp = timestamp;

                if (images.length <= 2) {
                    const marginBottom = container.clientHeight - (222 * images.length);
                    // console.log(container.clientHeight, marginBottom);

                    setMarginTop(prevPosition => {
                        const newPosition = prevPosition + (scrollUp ? scrollSpeed : (0-scrollSpeed));
                        
                        // If we've reached the bottom, reset to top
                        if (newPosition >= marginBottom) {
                            setScrollUp(false);
                            // console.log(newPosition);
                        }
                        if (newPosition <= 0) {
                            setScrollUp(true);
                        }
                        return newPosition;
                    });

                } else {
                    // Update scroll position
                    setScrollPosition(prevPosition => {
                        const newPosition = prevPosition + (scrollUp ? scrollSpeed : (0-scrollSpeed));
                        
                        // If we've reached the bottom, reset to top
                        if (newPosition >= scrollHeight) {
                            setScrollUp(false);
                        }
                        if (newPosition <= 0) {
                            setScrollUp(true);
                        }
                        // console.log(newPosition);
                        return newPosition;
                    });
                    
                    // Apply the scroll position
                    // console.log(scrollPosition)
                    container.scrollTop = scrollPosition;
                }              
            }
            
            animationFrameId = requestAnimationFrame(scrollStep);
        };
        
        animationFrameId = requestAnimationFrame(scrollStep);
        
        return () => {
            // console.log("cancel")
            cancelAnimationFrame(animationFrameId);
        };
    }, [scrollPosition, autoScroll, isUserScrolling, images, marginTop]);
  
    useEffect(() => {
        setTimeout(() => {
            if (images.length > 0) {
                handleClick(images[0]);
            }
        }, 1000);
    }, []);

    const handleClick = (param, e) => {
        setFilespec((filespec) => ({
            ...filespec,
            url: config.videoBaseUrl + param.video_id,
            video_id: param.video_id,
            title: param.title,
        }));
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    }

    const countText = (count) => {
        if (!count) return 0;
        if (count < 1000) return count;
        if (count < 1000000) return (count / 1000).toFixed(1) + 'K';
        if (count < 1000000000) return (count / 1000000).toFixed(1) + 'M';
        if (count < 1000000000000) return (count / 1000000000).toFixed(1) + 'B';
        return count;
    };

    const getCounts = (item) => {
        // if (!item.video_info) return '';
        // console.log(item);
        // console.log(user.playCounts[item.video_id]);

        let thisCount = 0;
        if (user.playCounts) {
            if (user.playCounts[item.video_id]) {
                thisCount = user.playCounts[item.video_id];
            }
        }
        return countText((item.video_info?.play_count || item.play_count) + thisCount ) + '/' + countText(item.video_info?.view_count || item.view_count);
    }

    return (
        // <div className="bg-gray-800 p-4 mt-4 rounded-lg h-142 w-96 mx-4">
        <div className="bg-gray-800 p-4 mt-4 rounded-lg min-h-[calc(66vh)] w-96 mx-4">
            <div 
                ref={containerRef}
                // container.scrollHeight depends on number of images
                // container.clientHeight = 121 x 4 = 484 + 16 + 16 = 516 / 4 = 129
                // container.clientWidth  = 384 / 4 = 96
                // className="h-134 overflow-hidden overscroll-contain touch-pan-y"
                className="max-h-[calc(64vh)] overflow-hidden overscroll-contain touch-pan-y"
                onMouseEnter={() => {
                    setAutoScroll(false);
                    setIsUserScrolling(true);
                    containerRef.current.className = "max-h-[calc(64vh)] overflow-y-auto overscroll-contain touch-pan-y"
                }}
                onMouseLeave={() => {
                    setAutoScroll(true);
                    setIsUserScrolling(false);
                    containerRef.current.className = "max-h-[calc(64vh)] overflow-y-hidden overscroll-contain touch-pan-y"
                }}
            >
                <div className="space-y-4 p-4"
                    style={{
                        marginTop: `${marginTop}px`
                    }}
                >
                    {images.map((item) => (
                        <div key={item.video_id}
                            className="overflow-hidden transition-all duration-300 hover:scale-103"
                            onClick={handleClick.bind(null, item)}
                        >
                            <ImageItem
                                video_id={item.video_id}
                                title={item.title}
                                image_src={item.video_info?.image_src || item.image_src}
                                alt={item.title}
                            />
                            <div className='flex justify-between'>
                                <label className="pl-2 text-gray-200 truncate text-nowrap">
                                    {item.title}
                                </label>
                                <label className="pr-2 text-gray-200">
                                    {getCounts(item)}
                                </label>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ImageGallery;

